// NumerosInsigne.js — Bloco "A Insigne em números" da home.
//
// O maior argumento da editora — quantos livros ela realmente publicou — não
// aparecia em lugar nenhum da home. Os números são CONTADOS do catálogo real
// (endpoint /ebook), nunca escritos à mão: número institucional digitado
// envelhece e vira mentira em duas semanas.
//
// A primeira versão era só três números soltos numa faixa. Ficou pobre, e
// pior: era decoração pura, sem serventia nenhuma para quem está procurando
// livro. Esta versão mantém os números (que dão credibilidade) e acrescenta o
// que faltava — as áreas com mais títulos, clicáveis, que levam direto ao
// catálogo já filtrado. Assim o bloco deixa de ser vitrine de vaidade e vira
// porta de entrada do acervo.
//
// Se a API não responder, a seção some. Vitrine com "0 livros publicados" é
// pior do que vitrine sem faixa de números.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CountUp from "../ui/CountUp";
import Reveal from "../ui/Reveal";
import RevealStagger from "../ui/RevealStagger";
import SpotlightCard from "../ui/SpotlightCard";
import BorderGlow from "../ui/BorderGlow";
import { TituloDeSecao } from "../ui/TituloDeSecao";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

/** Quantas áreas viram atalho. Mais que isso vira parede de etiquetas. */
const AREAS_EM_DESTAQUE = 6;

const NumerosInsigne = () => {
  const [dados, setDados] = useState(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const r = await fetch(API_URL + "ebook");
        if (!r.ok) return;
        const livros = await r.json();
        if (!ativo || !Array.isArray(livros) || livros.length === 0) return;

        // Autores distintos: o mesmo autor assina vários livros, então contamos
        // por id (ou pelo nome, quando a API devolve string).
        const autores = new Set();
        const porArea = new Map();

        livros.forEach((l) => {
          (Array.isArray(l.autores) ? l.autores : []).forEach((a) => {
            if (a == null) return;
            autores.add(typeof a === "object" ? `id:${a.id}` : `nome:${a}`);
          });
          (Array.isArray(l.categorias) ? l.categorias : []).forEach((c) => {
            const nome = typeof c === "string" ? c : c?.nome;
            if (nome) porArea.set(nome, (porArea.get(nome) || 0) + 1);
          });
        });

        const areasOrdenadas = [...porArea.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([nome, total]) => ({ nome, total }));

        setDados({
          livros: livros.length,
          autores: autores.size,
          areas: areasOrdenadas.length,
          topAreas: areasOrdenadas.slice(0, AREAS_EM_DESTAQUE),
        });
      } catch {
        /* sem dados → seção não aparece */
      }
    })();
    return () => { ativo = false; };
  }, []);

  if (!dados) return null;

  const metricas = [
    {
      icone: "bi-journal-bookmark-fill",
      valor: dados.livros,
      sufixo: "+",
      rotulo: "Títulos publicados",
      apoio: "Em formato impresso e digital",
    },
    {
      icone: "bi-people-fill",
      valor: dados.autores,
      sufixo: "+",
      rotulo: "Autores publicados",
      apoio: "Pesquisadores de todo o país",
    },
    {
      icone: "bi-diagram-3-fill",
      valor: dados.areas,
      sufixo: "",
      // "Categorias", e não "Áreas do conhecimento": a lista real do catálogo
      // mistura área (Direito, Educação) com tipo de obra (Coletânea) e com
      // origem (Eventos, Parceiros). Chamar tudo de área do conhecimento seria
      // vender o acervo com um rótulo que os próprios dados desmentem.
      rotulo: "Categorias no catálogo",
      apoio: "Do Direito às Engenharias",
    },
  ].filter((m) => m.valor > 0);

  return (
    <section className="px-4 md:px-8 py-10">
      <Reveal className="bloco relative max-w-[1280px] mx-auto overflow-hidden">
        {/* ===== Fundo decorativo =====
            Dois brilhos radiais no laranja da marca (via token, então funciona
            no claro e no escuro) e uma malha de pontos bem fraca. É textura de
            CSS: nada de WebGL, que come GPU e bateria por enfeite. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `
              radial-gradient(60% 120% at 8% 0%, rgb(var(--c-orange) / 0.12), transparent 60%),
              radial-gradient(50% 120% at 100% 100%, rgb(var(--c-orange) / 0.08), transparent 60%),
              radial-gradient(rgb(var(--c-ink) / 0.05) 1px, transparent 1px)`,
            backgroundSize: "auto, auto, 22px 22px",
          }}
        />

        <div className="relative px-6 py-9 md:px-10 md:py-11">
          {/* ===== Cabeçalho ===== */}
          <div className="max-w-[640px]">
            <TituloDeSecao
              chapeu="Nosso acervo"
              titulo="A Insigne Acadêmica em números"
              destaque="em números"
              subtitulo="Um catálogo construído livro a livro, com pesquisadores de várias regiões e áreas do conhecimento."
            />
          </div>

          {/* ===== Métricas ===== */}
          <RevealStagger className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {metricas.map((m) => (
              <RevealStagger.Item key={m.rotulo} className="h-full">
                {/* 8 = `rounded-card`, o mesmo do SpotlightCard logo abaixo.
                    Era 16, par do `rounded-2xl` antigo; com o cartão em 8px o
                    anel de luz ficava com o dobro do raio dele. */}
                <BorderGlow raio={4} className="h-full">
                <SpotlightCard className="h-full rounded-card border border-line bg-insigne-navy-deep/60
                                          p-5 transition-colors duration-300">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-controle
                                   bg-insigne-orange/15 text-insigne-orange text-lg">
                    <i className={"bi " + m.icone} />
                  </span>
                  <CountUp
                    to={m.valor}
                    sufixo={m.sufixo}
                    className="block mt-4 font-display text-[3.6rem] md:text-[4.4rem] text-insigne-orange tabular-nums leading-none"
                  />
                  <p className="text-white font-semibold text-sm mt-2">{m.rotulo}</p>
                  <p className="text-white/45 text-xs mt-0.5">{m.apoio}</p>
                </SpotlightCard>
                </BorderGlow>
              </RevealStagger.Item>
            ))}
          </RevealStagger>

          {/* ===== Áreas com mais títulos (atalhos para o catálogo) ===== */}
          {dados.topAreas.length > 0 && (
            <div className="mt-8 pt-7 border-t border-line">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/40 mb-3">
                Categorias com mais títulos
              </p>
              <div className="flex flex-wrap gap-2">
                {dados.topAreas.map((area) => (
                  <Link
                    key={area.nome}
                    to={main + "livros?categoria=" + encodeURIComponent(area.nome)}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    title={area.nome}
                    className="group/area inline-flex items-center gap-2 rounded-controle border border-line
                               bg-insigne-navy-deep/60 pl-3.5 pr-2 py-1.5 text-sm text-white/80
                               hover:border-insigne-orange hover:text-white transition-colors"
                  >
                    {/* Há categorias com nome longo ("Congresso de Direito
                        Aplicado - Org. Insigne Acadêmica"): sem teto de
                        largura, uma delas sozinha ocupava metade da linha. */}
                    <span className="max-w-[210px] truncate">{area.nome}</span>
                    <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5
                                     rounded-selo bg-insigne-orange/15 text-insigne-orange text-xs font-bold
                                     group-hover/area:bg-insigne-orange group-hover/area:text-paper
                                     transition-colors">
                      {area.total}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* ===== CTA ===== */}
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
            <Link
              to={main + "livros"}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="btn-solido inline-flex items-center justify-center rounded-controle
                         font-semibold text-sm px-8 py-3"
            >
              Explorar o catálogo
            </Link>
            <Link
              to={main + "autores"}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              // `border-white/20` e não `border-line`: o token `line` é um navy
              // discreto de propósito (para hairlines) e no tema escuro sumia
              // dentro do card — o botão parecia texto solto. O `white` é
              // temável, então a borda fica visível no claro e no escuro.
              className="inline-flex items-center justify-center rounded-controle border-2 border-white/20
                         text-white/80 font-semibold text-sm px-8 py-3
                         hover:border-insigne-orange hover:text-white transition-colors"
            >
              Conhecer os autores
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default NumerosInsigne;
